import React from 'react';
import { formatCurrency } from '../../utils/formatters';
import type { PriceScenario } from '../../types/calculations';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PriceScenarioGridProps {
  priceScenarios: PriceScenario[];
  offerPrice: number;
  marketValue: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function pct(v: number, decimals = 1): string {
  const val = v > 1 ? v : v * 100;
  return `${val.toFixed(decimals)}%`;
}

// ---------------------------------------------------------------------------
// Verdict badge
// ---------------------------------------------------------------------------

function VerdictBadge({ verdict }: { verdict: string }) {
  const v = verdict.toUpperCase();
  if (v.includes('GO') && !v.includes('NO')) {
    return (
      <span className="inline-block px-2 py-0.5 text-sm font-bold tracking-wider uppercase rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
        {v.includes('STRONG') ? 'STRONG GO' : 'GO'}
      </span>
    );
  }
  if (v.includes('CONDITIONAL')) {
    return (
      <span className="inline-block px-2 py-0.5 text-sm font-bold tracking-wider uppercase rounded bg-amber-500/15 text-amber-400 border border-amber-500/30">
        CONDITIONAL
      </span>
    );
  }
  return (
    <span className="inline-block px-2 py-0.5 text-sm font-bold tracking-wider uppercase rounded bg-red-500/15 text-red-400 border border-red-500/30">
      NO-GO
    </span>
  );
}

// ---------------------------------------------------------------------------
// Metric rows
// ---------------------------------------------------------------------------

type MetricRow = {
  label: string;
  key: keyof PriceScenario;
  format: 'currency' | 'pct' | 'mult' | 'verdict';
  highlight?: boolean;
};

const METRIC_ROWS: MetricRow[] = [
  { label: 'Purchase Price', key: 'purchase_price', format: 'currency' },
  { label: 'All-In Cost (+ CapEx)', key: 'all_in_cost', format: 'currency' },
  { label: 'Down Payment (25%)', key: 'down_payment', format: 'currency' },
  { label: 'Total Equity Required', key: 'equity_required', format: 'currency' },
  { label: 'Loan Amount', key: 'loan_amount', format: 'currency' },
  { label: 'LTV', key: 'ltv', format: 'pct' },
  { label: 'Entry Cap Rate', key: 'entry_cap_rate', format: 'pct' },
  { label: 'DSCR', key: 'dscr', format: 'mult' },
  { label: 'Cash-on-Cash (Yr 1)', key: 'coc_yr1', format: 'pct' },
  { label: 'Pre-Tax IRR (5yr)', key: 'pre_tax_irr_5yr', format: 'pct' },
  { label: 'Pre-Tax IRR (10yr)', key: 'pre_tax_irr_10yr', format: 'pct' },
  { label: 'Equity Multiple (5yr)', key: 'equity_multiple_5yr', format: 'mult' },
  { label: 'After-Tax IRR (5yr, OBBB)', key: 'after_tax_irr_5yr', format: 'pct', highlight: true },
  { label: 'After-Tax CoC (Yr 1, OBBB)', key: 'after_tax_coc_yr1', format: 'pct', highlight: true },
  { label: 'Yield on Cost', key: 'yield_on_cost', format: 'pct' },
  { label: 'Verdict', key: 'verdict', format: 'verdict' },
];

function formatValue(value: number | string, format: MetricRow['format']): string {
  if (format === 'currency') return formatCurrency(value as number);
  if (format === 'pct') return pct(value as number);
  if (format === 'mult') return `${(value as number).toFixed(2)}x`;
  return String(value);
}

function isBest(values: (number | string)[], index: number, format: MetricRow['format']): boolean {
  if (format === 'verdict') return false;
  const nums = values.map(Number);
  const max = Math.max(...nums);
  return nums[index] === max && nums.filter(n => n === max).length === 1;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const PriceScenarioGrid: React.FC<PriceScenarioGridProps> = ({
  priceScenarios,
  offerPrice,
  marketValue,
}) => {
  const discountPct = marketValue > 0 ? ((marketValue - offerPrice) / marketValue * 100).toFixed(0) : '0';

  return (
    <div className="bg-charcoal/60 border border-slate-dark rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-dark">
        <h3 className="text-sm font-semibold text-ivory">
          Price Scenario Comparison
        </h3>
        <p className="text-sm text-stone-700 mt-0.5">
          Side-by-side returns at different purchase prices — every metric recalculated per scenario
        </p>
      </div>

      {/* Grid */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-sm font-semibold tracking-widest uppercase border-b border-stone-800">
              <th className="text-left px-4 py-2.5 text-stone-700 w-[200px]">Metric</th>
              {priceScenarios.map((s, i) => (
                <th
                  key={s.label}
                  className={`text-right px-4 py-2.5 ${
                    i === 1 ? 'text-gold border-l-2 border-gold/30 bg-gold/[0.03]' : 'text-stone-700'
                  }`}
                >
                  {s.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {METRIC_ROWS.map((row) => {
              const values = priceScenarios.map(s => s[row.key]);
              return (
                <tr key={row.key} className="border-b border-stone-800/50 hover:bg-stone-800/20 transition-colors">
                  <td className={`px-4 py-2 font-medium ${row.highlight ? 'text-emerald-400' : 'text-stone-700'} ${row.key === 'verdict' ? 'font-bold text-ivory' : ''}`}>
                    {row.label}
                  </td>
                  {priceScenarios.map((_scenario, colIdx) => {
                    const val = values[colIdx];
                    const best = row.format !== 'verdict' && isBest(values as number[], colIdx, row.format);
                    const isMiddle = colIdx === 1;
                    return (
                      <td
                        key={colIdx}
                        className={`px-4 py-2 text-right tabular-nums ${
                          isMiddle ? 'border-l-2 border-gold/30 bg-gold/[0.03]' : ''
                        } ${best ? 'text-emerald-400 font-semibold' : 'text-stone-300'} ${
                          isMiddle && row.key === 'purchase_price' ? 'text-gold font-semibold' : ''
                        } ${isMiddle && row.key === 'equity_required' ? 'text-gold font-semibold' : ''}`}
                      >
                        {row.format === 'verdict' ? (
                          <VerdictBadge verdict={String(val)} />
                        ) : (
                          formatValue(val as number, row.format)
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Annotation */}
      <div className="px-4 py-3 border-t border-stone-800 bg-stone-900/40 text-sm text-stone-700">
        Off-Market price from SmartOffer engine ({formatCurrency(offerPrice)}, -{discountPct}% from OPA).
        Aggressive assumes 25% further discount. All returns solver-computed by backend.
      </div>
    </div>
  );
};
